import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { it, hrms } from '../../services/api';
import Card from '../../components/common/Card';
import Table from '../../components/common/Table';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { SearchableSelect } from '../../components/common/SearchableSelect';
import { theme } from '../../theme';

function ActionMenu({ row, onAssign, onTransfer, onReturn, navigate }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const menuItem = (label, color, onClick) => (
    <div
      onMouseDown={(e) => { e.preventDefault(); setOpen(false); onClick(); }}
      style={{ padding: '0.5rem 1rem', fontSize: '0.8rem', fontWeight: 500, color, cursor: 'pointer', whiteSpace: 'nowrap' }}
      onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
      onMouseLeave={e => e.currentTarget.style.background = ''}
    >{label}</div>
  );

  return (
    <div ref={ref} style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end', alignItems: 'center' }}>
      <Button size="sm" variant="outline" onClick={() => navigate(`/it/assets/${row.id}`)}>View</Button>
      <div style={{ position: 'relative' }}>
        <button
          onClick={() => setOpen(o => !o)}
          style={{ padding: '0.3rem 0.55rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.375rem', background: '#fff', cursor: 'pointer', fontSize: '1rem', lineHeight: 1, color: theme.textMuted, fontWeight: 700 }}
        >⋮</button>
        {open && (
          <div style={{ position: 'fixed', zIndex: 9999, background: '#fff', border: `1px solid ${theme.border}`, borderRadius: '0.5rem', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: '140px', overflow: 'hidden',
            top: (() => { const el = ref.current?.querySelector('button:last-child'); return el ? el.getBoundingClientRect().bottom + 4 : 0; })(),
            right: (() => { const el = ref.current?.querySelector('button:last-child'); return window.innerWidth - (el ? el.getBoundingClientRect().right : 0); })(),
          }}>
            {menuItem('Edit', theme.text, () => navigate(`/it/assets/${row.id}/edit`))}
            {row.status === 'available' && menuItem('Assign', theme.primary, () => onAssign(row))}
            {row.status === 'assigned' && menuItem('Transfer', theme.warning, () => onTransfer(row))}
            {row.status === 'assigned' && menuItem('Return', theme.success, () => onReturn(row))}
          </div>
        )}
      </div>
    </div>
  );
}

const ALL_STATUSES = [
  { value: 'available', label: 'Available' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'in_repair', label: 'In Repair' },
  { value: 'retired', label: 'Retired' },
  { value: 'lost', label: 'Lost' },
  { value: 'disposed', label: 'Disposed' },
];

function HealthBadge({ score, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
      <div style={{ width: '36px', height: '6px', borderRadius: '3px', background: theme.border, overflow: 'hidden' }}>
        <div style={{ width: `${score}%`, height: '100%', background: color, borderRadius: '3px' }} />
      </div>
      <span style={{ fontSize: '0.75rem', fontWeight: 700, color }}>{score}</span>
    </div>
  );
}

function AssetList() {
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];

  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');

  const [assignModal, setAssignModal] = useState(null);
  const [assignForm, setAssignForm] = useState({ employee_id: '', assigned_date: today, notes: '' });
  const [transferModal, setTransferModal] = useState(null);
  const [transferForm, setTransferForm] = useState({ employee_id: '', assigned_date: today, notes: '' });
  const [saving, setSaving] = useState(false);

  // Bulk import
  const [importModal, setImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterCat) params.category_id = filterCat;
      if (filterStatus) params.status = filterStatus;
      const [ar, cr, em] = await Promise.allSettled([
        it.getAssets(params),
        it.getAssetCategories(),
        hrms.getEmployees({ limit: 500 }),
      ]);
      if (ar.status === 'fulfilled') { const d = ar.value.data; setAssets(Array.isArray(d) ? d : []); }
      if (cr.status === 'fulfilled') setCategories(cr.value.data || []);
      if (em.status === 'fulfilled') { const d = em.value.data; setEmployees(Array.isArray(d) ? d : d.items || []); }
    } catch { } finally { setLoading(false); }
  }, [search, filterCat, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const handleAssign = async () => {
    if (!assignForm.employee_id || !assignForm.assigned_date) { alert('Please select an employee and date.'); return; }
    setSaving(true);
    try {
      await it.assignAsset(assignModal.id, { ...assignForm, employee_id: parseInt(assignForm.employee_id) });
      setAssignModal(null); setAssignForm({ employee_id: '', assigned_date: today, notes: '' }); load();
    } catch (err) { alert(err?.response?.data?.detail || 'Failed to assign.'); } finally { setSaving(false); }
  };

  const handleReturn = async (asset) => {
    if (!window.confirm(`Return "${asset.name}" from current user?`)) return;
    try {
      await it.returnAsset(asset.id, { return_date: today, return_reason: 'Returned via admin panel' });
      load();
    } catch { alert('Failed to return asset.'); }
  };

  const handleTransfer = async () => {
    if (!transferForm.employee_id || !transferForm.assigned_date) { alert('Please select an employee and date.'); return; }
    setSaving(true);
    try {
      await it.transferAsset(transferModal.id, {
        to_employee_id: parseInt(transferForm.employee_id),
        transfer_date: transferForm.assigned_date,
        reason: transferForm.notes || null,
      });
      setTransferModal(null); setTransferForm({ employee_id: '', assigned_date: today, notes: '' }); load();
    } catch (err) { alert(err?.response?.data?.detail || 'Failed to transfer.'); } finally { setSaving(false); }
  };

  const handleImport = async () => {
    if (!importFile) { alert('Select a CSV file first.'); return; }
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const res = await it.bulkImportAssets(fd);
      setImportResult(res.data);
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Import failed.');
    } finally { setImporting(false); }
  };

  const handleExportCSV = async () => {
    try {
      const res = await it.exportAssetsCSV();
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `assets_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { alert('Export failed.'); }
  };

  const downloadTemplate = () => {
    const headers = 'name,category,make,model,serial_number,purchase_date,purchase_cost,warranty_expiry,location,cpu,ram_gb,storage_gb,os,screen_size,notes';
    const example = 'Dell Latitude 5530,Laptop,Dell,Latitude 5530,SN123456,2024-01-15,,2027-01-15,Floor 2,Intel i7-1265U,16,512,Windows 11 Pro,14",';
    const blob = new Blob([headers + '\n' + example], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'asset_import_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const inp = { width: '100%', padding: '0.6rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', marginBottom: '0.75rem', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.text, marginBottom: '0.35rem' };

  const columns = [
    {
      header: 'Asset', key: 'asset_code',
      render: (v, row) => (
        <div>
          <code style={{ background: theme.pageBg, padding: '0.15rem 0.4rem', borderRadius: '0.25rem', fontSize: '0.75rem', border: `1px solid ${theme.border}` }}>{v}</code>
          <p style={{ margin: '0.2rem 0 0', fontWeight: 600, fontSize: '0.875rem' }}>{row.name}</p>
          <p style={{ margin: 0, fontSize: '0.72rem', color: theme.textMuted }}>{row.category_name}</p>
        </div>
      ),
    },
    {
      header: 'Make / Model', key: 'make',
      render: (v, row) => (
        <div>
          <p style={{ margin: 0, fontSize: '0.875rem' }}>{v || '—'}</p>
          <p style={{ margin: 0, fontSize: '0.75rem', color: theme.textMuted }}>{row.model || ''}</p>
        </div>
      ),
    },
    {
      header: 'Category', key: 'category_name',
      render: (v) => v
        ? <span style={{ display: 'inline-block', padding: '0.15rem 0.55rem', borderRadius: 20, fontSize: '0.72rem', fontWeight: 600, background: `${theme.primary}15`, color: theme.primary }}>{v}</span>
        : <span style={{ color: theme.textMuted, fontSize: '0.8rem' }}>—</span>,
    },
    { header: 'Assigned To', key: 'current_assignment', render: (v) => v?.employee_name || <span style={{ color: theme.textMuted, fontSize: '0.8rem' }}>Unassigned</span> },
    { header: 'Warranty', key: 'warranty_expiry', render: (v) => {
      if (!v) return <span style={{ color: theme.textMuted }}>—</span>;
      const days = Math.floor((new Date(v + 'T12:00:00') - new Date()) / 86400000);
      const color = days < 0 ? theme.danger : days < 60 ? theme.warning : theme.success;
      return <span style={{ fontSize: '0.8rem', fontWeight: 600, color }}>{days < 0 ? 'Expired' : `${days}d left`}</span>;
    }},
    { header: 'Health', key: 'health_score', render: (v, row) => v != null ? <HealthBadge score={v} label={row.health_label} color={row.health_color} /> : '—' },
    { header: 'Status', key: 'status', render: (v) => <Badge status={v || 'available'} /> },
    {
      header: 'Actions', key: 'id', align: 'right',
      render: (_, row) => (
        <ActionMenu
          row={row}
          navigate={navigate}
          onAssign={(r) => { setAssignModal(r); setAssignForm({ employee_id: '', assigned_date: today, notes: '' }); }}
          onTransfer={(r) => { setTransferModal(r); setTransferForm({ employee_id: '', assigned_date: today, notes: '' }); }}
          onReturn={handleReturn}
        />
      ),
    },
  ];

  // Client-side employee filter (assets already loaded; assignment data is in each row)
  const displayedAssets = filterEmployee
    ? assets.filter(a => String(a.current_assignment?.employee_id) === String(filterEmployee))
    : assets;

  // Build employee options from assets that are currently assigned
  const assignedEmployeeOptions = (() => {
    const seen = new Set();
    const opts = [];
    assets.forEach(a => {
      const asgn = a.current_assignment;
      if (asgn?.employee_id && !seen.has(asgn.employee_id)) {
        seen.add(asgn.employee_id);
        opts.push({ value: asgn.employee_id, label: asgn.employee_name });
      }
    });
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  })();

  const fltStyle = { padding: '0.5rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', background: theme.white };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>IT Assets</h1>
          <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>{displayedAssets.length} assets</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <Button variant="outlineGray" onClick={handleExportCSV}>⬇ Export CSV</Button>
          <Button variant="outlineGray" onClick={() => { setImportModal(true); setImportFile(null); setImportResult(null); }}>⬆ Import CSV</Button>
          <Button variant="primary" onClick={() => navigate('/it/assets/new')}>+ Add Asset</Button>
        </div>
      </div>

      {/* Filters */}
      <Card padding="1rem">
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input type="text" placeholder="Search by name, code, serial..." value={search} onChange={(e) => setSearch(e.target.value)}
            style={{ ...fltStyle, flex: 1, minWidth: '200px' }} />
          <SearchableSelect
            options={categories.map(c => ({ value: c.id, label: c.name }))}
            value={filterCat}
            onChange={setFilterCat}
            placeholder="All Categories"
            style={{ minWidth: 180 }}
          />
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={fltStyle}>
            <option value="">All Status</option>
            {ALL_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <SearchableSelect
            options={assignedEmployeeOptions}
            value={filterEmployee}
            onChange={setFilterEmployee}
            placeholder="All Employees"
            style={{ minWidth: 200 }}
          />
          <Button variant="outlineGray" size="sm" onClick={() => { setSearch(''); setFilterCat(''); setFilterStatus(''); setFilterEmployee(''); }}>Clear</Button>
        </div>
      </Card>

      <Card padding="0">
        <Table columns={columns} data={displayedAssets} loading={loading} emptyMessage="No assets found" />
      </Card>

      {/* Assign Modal */}
      <Modal isOpen={!!assignModal} onClose={() => setAssignModal(null)} title={`Assign: ${assignModal?.name}`}
        footer={<><Button variant="outlineGray" onClick={() => setAssignModal(null)}>Cancel</Button><Button variant="primary" loading={saving} onClick={handleAssign}>Assign</Button></>}>
        <label style={lbl}>Assign To *</label>
        <select style={inp} value={assignForm.employee_id} onChange={(e) => setAssignForm({ ...assignForm, employee_id: e.target.value })}>
          <option value="">Select employee</option>
          {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name} ({e.emp_code})</option>)}
        </select>
        <label style={lbl}>Assigned Date *</label>
        <input style={inp} type="date" value={assignForm.assigned_date} onChange={(e) => setAssignForm({ ...assignForm, assigned_date: e.target.value })} />
        <label style={lbl}>Notes</label>
        <textarea style={{ ...inp, resize: 'vertical', minHeight: '70px' }} value={assignForm.notes} onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })} />
      </Modal>

      {/* Transfer Modal */}
      <Modal isOpen={!!transferModal} onClose={() => setTransferModal(null)} title={`Transfer: ${transferModal?.name}`}
        footer={<><Button variant="outlineGray" onClick={() => setTransferModal(null)}>Cancel</Button><Button variant="warning" loading={saving} onClick={handleTransfer}>Transfer</Button></>}>
        <p style={{ fontSize: '0.875rem', color: theme.textMuted, marginBottom: '1rem' }}>
          Currently assigned to: <strong>{transferModal?.current_assignment?.employee_name || '—'}</strong>
        </p>
        <label style={lbl}>Transfer To *</label>
        <select style={inp} value={transferForm.employee_id} onChange={(e) => setTransferForm({ ...transferForm, employee_id: e.target.value })}>
          <option value="">Select employee</option>
          {employees.filter((e) => e.id !== transferModal?.current_assignment?.employee_id).map((e) => <option key={e.id} value={e.id}>{e.full_name} ({e.emp_code})</option>)}
        </select>
        <label style={lbl}>Transfer Date *</label>
        <input style={inp} type="date" value={transferForm.assigned_date} onChange={(e) => setTransferForm({ ...transferForm, assigned_date: e.target.value })} />
        <label style={lbl}>Notes</label>
        <textarea style={{ ...inp, resize: 'vertical', minHeight: '70px' }} value={transferForm.notes} onChange={(e) => setTransferForm({ ...transferForm, notes: e.target.value })} />
      </Modal>

      {/* Bulk Import Modal */}
      <Modal isOpen={importModal} onClose={() => setImportModal(false)} title="Bulk Import Assets"
        footer={
          <div style={{ display: 'flex', gap: '0.75rem', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button variant="outlineGray" size="sm" onClick={downloadTemplate}>⬇ Download Template</Button>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <Button variant="outlineGray" onClick={() => setImportModal(false)}>Close</Button>
              <Button variant="primary" loading={importing} onClick={handleImport}>Import</Button>
            </div>
          </div>
        }>
        <p style={{ fontSize: '0.875rem', color: theme.textMuted, marginBottom: '1rem' }}>
          Upload a CSV file to bulk-add assets. Download the template to see the required format.
          Required column: <strong>name</strong>. All others are optional.
        </p>

        <div style={{ border: `2px dashed ${theme.border}`, borderRadius: '0.625rem', padding: '1.5rem', textAlign: 'center', cursor: 'pointer', marginBottom: '1rem' }}
          onClick={() => fileRef.current?.click()}>
          <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={(e) => setImportFile(e.target.files[0])} />
          {importFile ? (
            <div>
              <p style={{ fontSize: '1.5rem', margin: 0 }}>📄</p>
              <p style={{ fontWeight: 600, color: theme.text, margin: '0.5rem 0 0' }}>{importFile.name}</p>
              <p style={{ fontSize: '0.8rem', color: theme.textMuted, margin: '0.25rem 0 0' }}>Click to change file</p>
            </div>
          ) : (
            <div>
              <p style={{ fontSize: '1.75rem', margin: 0 }}>⬆</p>
              <p style={{ fontWeight: 600, color: theme.text, margin: '0.5rem 0 0' }}>Click to select CSV file</p>
              <p style={{ fontSize: '0.8rem', color: theme.textMuted, margin: '0.25rem 0 0' }}>Only .csv files accepted</p>
            </div>
          )}
        </div>

        {importResult && (
          <div style={{ borderRadius: '0.5rem', overflow: 'hidden', border: `1px solid ${theme.border}` }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
              {[['Created', importResult.created, theme.success], ['Skipped', importResult.skipped, theme.warning], ['Errors', importResult.errors, theme.danger]].map(([label, count, color]) => (
                <div key={label} style={{ padding: '0.875rem', textAlign: 'center', background: `${color}15` }}>
                  <p style={{ fontSize: '1.5rem', fontWeight: 700, color, margin: 0 }}>{count}</p>
                  <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: '0.2rem 0 0' }}>{label}</p>
                </div>
              ))}
            </div>
            {importResult.details?.errors?.length > 0 && (
              <div style={{ padding: '0.75rem 1rem', borderTop: `1px solid ${theme.border}` }}>
                <p style={{ fontSize: '0.75rem', fontWeight: 700, color: theme.danger, margin: '0 0 0.4rem' }}>Errors:</p>
                {importResult.details.errors.map((e, i) => (
                  <p key={i} style={{ fontSize: '0.75rem', color: theme.danger, margin: '0.2rem 0' }}>Row {e.row}: {e.reason}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default AssetList;
